import type { AxiosResponse } from 'axios'

import type { ReportDetail, ReportDictionaryItem, ReportTaskListItem } from '@/api/schemas/reports/common'
import type {
    FindTaskPayloadSchema,
    GetScheduledTasksPayloadSchema,
    OperatorReportingInterruptedReport,
    OperatorReportingOrderJobListItem,
    OperatorReportingReportItemOrder,
    OperatorReportingReportItemOrdersFilters,
    OperatorReportingSession,
    OperatorReportingShippingGroup,
    OperatorReportingWorkspace,
    OperatorReportingWorkspaceListItem
} from '@/api/schemas/reports/operatorReporting'
import type { DetailedReportsResponseSchema } from '@/api/schemas/reports/review'
import {
    clearReportingWorkspace,
    loadOperatorReportingWorkspace,
    persistReportingWorkspace
} from '@/composables/reports/useReportingWorkspace'
import { REPORTS } from '@/constants'
import { useReportExecutionV2Store } from '@/stores'
import { operatorReportingService } from '@/views/ReportsV2/services/operatorReportingService'

import { createReportingParameterColumnsLoader } from '../shared/reportingParameterColumns'
import type { OperatorReportingCreateContext } from '../reportExecution/types'
import {
    createInitialReportItemOrdersFilters,
    type OperatorReportingState,
    type OperatorRunningReport,
    type ProcessReportSwitchRequest
} from './state'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createOperatorReportingActions(state: OperatorReportingState) {
    const reportExecutionStore = useReportExecutionV2Store()
    let latestTaskSearchRequestId = 0
    let latestScheduledTasksRequestId = 0
    let latestShippingGroupsRequestId = 0
    let latestReportItemOrdersRequestId = 0

    const invalidateScheduledTasksRequests = (): void => {
        latestScheduledTasksRequestId += 1
    }

    const invalidateOrdersBranchRequests = (): void => {
        latestShippingGroupsRequestId += 1
        latestReportItemOrdersRequestId += 1
    }

    const invalidateTaskSearch = (): void => {
        latestTaskSearchRequestId += 1
        setTaskSearchLoading(false)
    }

    const initializeWorkspaceSelection = (): void => {
        const storedWorkspace = loadOperatorReportingWorkspace()
        state.selectedWorkspaceId.value = storedWorkspace?.workspaceId ?? null
    }

    const setAvailableWorkspaces = (workspaces: OperatorReportingWorkspaceListItem[]): void => {
        state.availableWorkspaces.value = workspaces
    }

    const setAvailableWorkspacesLoading = (loading: boolean): void => {
        state.availableWorkspacesLoading.value = loading
    }

    const setBootstrapLoading = (loading: boolean): void => {
        state.bootstrapLoading.value = loading
    }

    const setSelectedWorkspace = (workspace: OperatorReportingWorkspace | null): void => {
        state.selectedWorkspace.value = workspace
        state.selectedWorkspaceId.value = workspace?.id ?? null

        persistReportingWorkspace(REPORTS.WORKSPACE_STORAGE_KEYS.OPERATOR, workspace?.id ?? null)
    }

    const setSelectedWorkspaceDetails = (workspace: OperatorReportingWorkspace | null): void => {
        state.selectedWorkspace.value = workspace
    }

    const setSelectedWorkspaceId = (workspaceId: number | null): void => {
        state.selectedWorkspaceId.value = workspaceId

        persistReportingWorkspace(REPORTS.WORKSPACE_STORAGE_KEYS.OPERATOR, workspaceId)
    }

    const setActiveSession = (session: OperatorReportingSession | null): void => {
        state.activeSession.value = session
    }

    const setUnfinishedReport = (report: OperatorReportingInterruptedReport | null): void => {
        state.unfinishedReport.value = report
    }

    const setInterruptedReports = (reports: ReportDetail[]): void => {
        state.interruptedReports.value = reports
    }

    const setInterruptedReportsLoading = (loading: boolean): void => {
        state.interruptedReportsLoading.value = loading
    }

    const setReportOrderJobs = (jobs: OperatorReportingOrderJobListItem[]): void => {
        state.reportOrderJobs.value = jobs
    }

    const setReportOrderJobsLoading = (loading: boolean): void => {
        state.reportOrderJobsLoading.value = loading
    }

    const setShippingGroups = (shippingGroups: OperatorReportingShippingGroup[]): void => {
        state.shippingGroups.value = shippingGroups
    }

    const setReportItemOrders = (orders: OperatorReportingReportItemOrder[]): void => {
        state.reportItemOrders.value = orders
    }

    const setReportItemOrdersLoading = (loading: boolean): void => {
        state.reportItemOrdersLoading.value = loading
    }

    const setReportItemOrdersFilters = (filters: OperatorReportingReportItemOrdersFilters): void => {
        state.reportItemOrdersFilters.value = {
            ...filters
        }
    }

    const setScheduledTasks = (tasks: ReportTaskListItem[]): void => {
        state.scheduledTasks.value = tasks
    }

    const setScheduledTasksLoading = (loading: boolean): void => {
        state.scheduledTasksLoading.value = loading
    }

    const setTaskSearchResults = (tasks: ReportTaskListItem[]): void => {
        state.taskSearchResults.value = tasks
    }

    const setTaskSearchLoading = (loading: boolean): void => {
        state.taskSearchLoading.value = loading
    }

    const setReportProcesses = (processes: ReportDictionaryItem[]): void => {
        state.reportProcesses.value = processes
    }

    const setOutOfSequenceCodes = (codes: ReportDictionaryItem[]): void => {
        state.outOfSequenceCodes.value = codes
    }

    const setSelectedTask = (task: ReportTaskListItem | null): void => {
        state.selectedTask.value = task
    }

    const setTaskSearch = (value: string | null): void => {
        state.taskSearch.value = value
    }

    const clearSelectedTaskContext = (): void => {
        state.selectedTask.value = null
        reportExecutionStore.setOperatorReportingCreateContext(null)
    }

    const resetReportItemOrdersFilters = (): void => {
        setReportItemOrdersFilters(createInitialReportItemOrdersFilters())
    }

    const resetOrdersBranchState = (): void => {
        invalidateOrdersBranchRequests()
        state.shippingGroups.value = []
        state.reportItemOrders.value = []
        state.reportItemOrdersLoading.value = false
        resetReportItemOrdersFilters()
    }

    const clearWorkspaceSelection = (): void => {
        state.selectedWorkspaceId.value = null
        state.selectedWorkspace.value = null

        clearReportingWorkspace(REPORTS.WORKSPACE_STORAGE_KEYS.OPERATOR)
    }

    const loadAvailableWorkspaces = async (): Promise<OperatorReportingWorkspaceListItem[]> => {
        setAvailableWorkspacesLoading(true)

        try {
            const response = await operatorReportingService.fetchOperatorWorkspaces()
            const workspaces = response.data?.results || []

            setAvailableWorkspaces(workspaces)

            return workspaces
        } finally {
            setAvailableWorkspacesLoading(false)
        }
    }

    const loadSelectedWorkspace = async (): Promise<OperatorReportingWorkspace | null> => {
        if (!state.selectedWorkspaceId.value) {
            setSelectedWorkspaceDetails(null)
            return null
        }

        try {
            const response = await operatorReportingService.fetchWorkspace(state.selectedWorkspaceId.value)
            const workspace = response.data || null

            setSelectedWorkspaceDetails(workspace)

            return workspace
        } catch (error) {
            setSelectedWorkspaceDetails(null)
            throw error
        }
    }

    const selectWorkspace = async (workspaceId: number): Promise<OperatorReportingWorkspace | null> => {
        const response = await operatorReportingService.fetchWorkspace(workspaceId)
        const workspace = response.data || null

        setSelectedWorkspace(workspace)
        invalidateTaskSearch()
        invalidateScheduledTasksRequests()
        setScheduledTasks([])
        resetOrdersBranchState()
        setTaskSearchResults([])
        setTaskSearch(null)
        setReportProcesses([])
        setOutOfSequenceCodes([])
        clearSelectedTaskContext()

        return workspace
    }

    const loadUnfinishedSession = async (userId: number): Promise<OperatorReportingSession | null> => {
        const response = await operatorReportingService.fetchUnfinishedReportsShiftSummary(userId)
        const session = operatorReportingService.mapSessionResponse(response.data?.results?.[0] || null)

        setActiveSession(session)

        return session
    }

    const loadUnfinishedReport = async (): Promise<OperatorReportingInterruptedReport | null> => {
        const reportShiftSummaryId = state.activeSession.value?.id

        if (!reportShiftSummaryId) {
            setUnfinishedReport(null)
            return null
        }

        const response = await operatorReportingService.fetchCurrentUnfinishedReport(reportShiftSummaryId)
        const report = operatorReportingService.normalizeInterruptedReport(response.data)

        setUnfinishedReport(report)

        return report
    }

    const loadInterruptedReports = async (queryString = ''): Promise<AxiosResponse<DetailedReportsResponseSchema> | null> => {
        const reportShiftSummaryId = state.activeSession.value?.id
        const machineId = state.selectedWorkspace.value?.machine?.id

        if (!reportShiftSummaryId || !machineId) {
            setInterruptedReports([])
            return null
        }

        setInterruptedReportsLoading(true)

        try {
            const response = await operatorReportingService.fetchInterruptedReports(reportShiftSummaryId, machineId, queryString)
            const reports = response.data?.results || []

            setInterruptedReports(reports)

            return response
        } finally {
            setInterruptedReportsLoading(false)
        }
    }

    const loadHomeInitialData = async (userId: number | null): Promise<void> => {
        setBootstrapLoading(true)
        setAvailableWorkspaces([])
        resetRuntimeContext()
        setSelectedWorkspaceDetails(null)

        try {
            await loadAvailableWorkspaces()

            if (state.selectedWorkspaceId.value) {
                await loadSelectedWorkspace()
            } else {
                setSelectedWorkspaceDetails(null)
            }

            if (!userId) {
                setActiveSession(null)
                setUnfinishedReport(null)
                return
            }

            const session = await loadUnfinishedSession(userId)

            if (!session) {
                setUnfinishedReport(null)
                return
            }

            await loadUnfinishedReport()
        } finally {
            setBootstrapLoading(false)
        }
    }

    // The active report reuses what home loaded and fetches only what is missing, e.g. after a page refresh.
    const ensureOperatorActionsContext = async (userId: number | null): Promise<void> => {
        if (state.bootstrapLoading.value) {
            return
        }

        if (!state.selectedWorkspaceId.value) {
            initializeWorkspaceSelection()
        }

        const requests: Promise<unknown>[] = []

        if (!state.availableWorkspaces.value.length) {
            requests.push(loadAvailableWorkspaces())
        }

        if (state.selectedWorkspaceId.value && state.selectedWorkspace.value?.id !== state.selectedWorkspaceId.value) {
            requests.push(loadSelectedWorkspace())
        }

        if (userId && !state.activeSession.value) {
            requests.push(loadUnfinishedSession(userId))
        }

        await Promise.all(requests)
    }

    const notifyPlanChanged = (): void => {
        state.planChangesVersion.value += 1
    }

    const setOperatorActionsDialogOpen = (open: boolean): void => {
        state.operatorActionsDialogOpen.value = open
    }

    const setRunningReport = (report: OperatorRunningReport | null): void => {
        state.runningReport.value = report
    }

    const requestProcessReportSwitch = (request: ProcessReportSwitchRequest): void => {
        state.processReportSwitchRequest.value = request
    }

    const takeProcessReportSwitchRequest = (): ProcessReportSwitchRequest | null => {
        const request = state.processReportSwitchRequest.value

        state.processReportSwitchRequest.value = null

        return request
    }

    // Starting the machine stop activity opens the accident form once its report is shown.
    const requestAccidentForm = (): void => {
        state.accidentFormRequested.value = true
    }

    const takeAccidentFormRequest = (): boolean => {
        const requested = state.accidentFormRequested.value

        state.accidentFormRequested.value = false

        return requested
    }

    const startReportingSession = async (): Promise<OperatorReportingSession> => {
        const response = await operatorReportingService.startReportingSession()
        const session = operatorReportingService.mapSessionResponse(response.data)

        setActiveSession(session)
        setUnfinishedReport(null)
        setInterruptedReports([])

        return session as OperatorReportingSession
    }

    const stopReportingSession = async (): Promise<OperatorReportingSession | null> => {
        const reportShiftSummaryId = state.activeSession.value?.id

        if (!reportShiftSummaryId) {
            return null
        }

        const response = await operatorReportingService.stopReportingSession(
            reportShiftSummaryId,
            REPORTS.REPORT_SHIFT_SUMMARY_STATUSES.FINISHED
        )
        const session = operatorReportingService.mapSessionResponse(response.data)

        return session
    }

    const finalizeStoppedSession = (): void => {
        setActiveSession(null)
        setUnfinishedReport(null)
        setInterruptedReports([])
        invalidateTaskSearch()
        invalidateScheduledTasksRequests()
        setScheduledTasks([])
        setTaskSearchResults([])
        setTaskSearch(null)
        setReportProcesses([])
        setOutOfSequenceCodes([])
        clearSelectedTaskContext()
    }

    const loadStoppedSessionDetails = async (reportShiftSummaryId: number): Promise<OperatorReportingInterruptedReport[]> => {
        const response = await operatorReportingService.fetchStoppedSessionDetails(reportShiftSummaryId)

        return response.data?.results || []
    }

    const getAllowedReportItemTypeIds = (): number[] => {
        const allowedReportItemTypes = state.selectedWorkspace.value?.allowedReportItemTypes || []

        const ids = allowedReportItemTypes
            .map((item) => {
                if (item.reportItemTypeId !== null && item.reportItemTypeId !== undefined) {
                    return Number(item.reportItemTypeId)
                }

                return item.reportItemType?.id ?? null
            })
            .filter((id): id is number => Number.isFinite(id))

        return [...new Set(ids)]
    }

    const loadScheduledTasks = async (
        payload?: Partial<Pick<GetScheduledTasksPayloadSchema, 'limit' | 'sortByPriority'>>
    ): Promise<ReportTaskListItem[]> => {
        const productionSiteId = state.selectedWorkspace.value?.id
        const machineCode = state.selectedWorkspace.value?.machine?.code

        if (!productionSiteId || !machineCode) {
            setScheduledTasks([])
            return []
        }

        setScheduledTasksLoading(true)
        const requestId = ++latestScheduledTasksRequestId
        const workspaceSnapshot = {
            productionSiteId,
            machineCode,
            sessionId: state.activeSession.value?.id ?? null
        }

        try {
            const response = await operatorReportingService.fetchScheduledTasks({
                productionSiteId,
                machineCode,
                withReportingParameters: true,
                ...payload
            })
            const tasks = response.data?.results || []

            const isCurrentRequest =
                requestId === latestScheduledTasksRequestId &&
                workspaceSnapshot.productionSiteId === state.selectedWorkspace.value?.id &&
                workspaceSnapshot.machineCode === state.selectedWorkspace.value?.machine?.code &&
                workspaceSnapshot.sessionId === (state.activeSession.value?.id ?? null)

            if (isCurrentRequest) {
                setScheduledTasks(tasks)
            }

            return tasks
        } finally {
            if (requestId === latestScheduledTasksRequestId) {
                setScheduledTasksLoading(false)
            }
        }
    }

    const loadReportingParameterColumns = createReportingParameterColumnsLoader(
        state,
        () => state.selectedWorkspace.value?.id ?? null,
        (payload) => operatorReportingService.fetchReportingParameters(payload)
    )

    const loadReportProcesses = async (): Promise<ReportDictionaryItem[]> => {
        const productionSiteId = state.selectedWorkspace.value?.id

        if (!productionSiteId) {
            setReportProcesses([])
            return []
        }

        const response = await operatorReportingService.fetchReportProcesses(productionSiteId)
        const processes = response.data?.results || []

        setReportProcesses(processes)

        return processes
    }

    const loadOutOfSequenceCodes = async (): Promise<ReportDictionaryItem[]> => {
        const response = await operatorReportingService.fetchReportOutOfSequenceCodes()
        const codes = response.data?.results || []

        setOutOfSequenceCodes(codes)

        return codes
    }

    const loadShippingGroups = async (): Promise<OperatorReportingShippingGroup[]> => {
        const requestId = ++latestShippingGroupsRequestId
        const workspaceId = state.selectedWorkspace.value?.id ?? null
        const sessionId = state.activeSession.value?.id ?? null
        const response = await operatorReportingService.fetchShippingGroups()
        const shippingGroups = operatorReportingService.mapShippingGroups(response.data)

        const isCurrentRequest =
            requestId === latestShippingGroupsRequestId &&
            workspaceId === (state.selectedWorkspace.value?.id ?? null) &&
            sessionId === (state.activeSession.value?.id ?? null)

        if (isCurrentRequest) {
            setShippingGroups(shippingGroups)
        }

        return shippingGroups
    }

    const loadReportItemOrders = async (): Promise<OperatorReportingReportItemOrder[]> => {
        const productionSiteId = state.selectedWorkspace.value?.id

        if (!productionSiteId) {
            setReportItemOrders([])
            return []
        }

        setReportItemOrdersLoading(true)
        const requestId = ++latestReportItemOrdersRequestId
        const requestFilters = {
            ...state.reportItemOrdersFilters.value
        }
        const sessionId = state.activeSession.value?.id ?? null

        try {
            const response = await operatorReportingService.fetchReportItemOrders(productionSiteId, requestFilters)
            const orders = operatorReportingService.mapReportItemOrders(response.data)

            const isCurrentRequest =
                requestId === latestReportItemOrdersRequestId &&
                productionSiteId === state.selectedWorkspace.value?.id &&
                sessionId === (state.activeSession.value?.id ?? null) &&
                requestFilters.shippingGroupCodes === state.reportItemOrdersFilters.value.shippingGroupCodes

            if (isCurrentRequest) {
                setReportItemOrders(orders)
            }

            return orders
        } finally {
            if (requestId === latestReportItemOrdersRequestId) {
                setReportItemOrdersLoading(false)
            }
        }
    }

    const searchTasks = async (taskNumber: string): Promise<ReportTaskListItem[]> => {
        const normalizedTaskNumber = taskNumber.trim()

        if (!normalizedTaskNumber) {
            invalidateTaskSearch()
            setTaskSearchResults([])
            return []
        }

        const requestId = ++latestTaskSearchRequestId

        const allowedTypeIds = getAllowedReportItemTypeIds()
        const payload: FindTaskPayloadSchema = {
            taskNumber: normalizedTaskNumber,
            typeId: allowedTypeIds.join(',') || null,
            elastic: Boolean(state.selectedWorkspace.value?.reportElastic)
        }

        setTaskSearchLoading(true)

        try {
            const response = await operatorReportingService.findTasks(payload)
            const tasks = response.data?.results || []

            if (requestId === latestTaskSearchRequestId) {
                setTaskSearchResults(tasks)
            }

            return tasks
        } finally {
            if (requestId === latestTaskSearchRequestId) {
                setTaskSearchLoading(false)
            }
        }
    }

    const searchTaskByNumber = async (taskNumber: string): Promise<ReportTaskListItem[]> => {
        const normalizedTaskNumber = taskNumber.trim()

        if (!normalizedTaskNumber) {
            return []
        }

        const allowedTypeIds = getAllowedReportItemTypeIds()
        const payload: FindTaskPayloadSchema = {
            taskNumber: normalizedTaskNumber,
            typeId: allowedTypeIds.join(',') || null,
            elastic: Boolean(state.selectedWorkspace.value?.reportElastic)
        }

        const response = await operatorReportingService.findTaskByNumber(payload)

        return response.data?.results || []
    }

    const loadReportOrderJobs = async (procNumber: string): Promise<OperatorReportingOrderJobListItem[]> => {
        const normalizedProcNumber = procNumber.trim()

        if (!normalizedProcNumber) {
            setReportOrderJobs([])
            return []
        }

        setReportOrderJobsLoading(true)

        try {
            const response = await operatorReportingService.fetchReportItemOrderJobs(normalizedProcNumber)
            const jobs = operatorReportingService.mapOrderJobs(response.data)

            setReportOrderJobs(jobs)

            return jobs
        } finally {
            setReportOrderJobsLoading(false)
        }
    }

    const saveCreateReportContext = (
        task: ReportTaskListItem,
        payload?: {
            plateNumber?: string | null
            processId?: number | null
            processName?: string | null
            outOfSequenceCodeId?: number | null
        }
    ): OperatorReportingCreateContext => {
        const taskNumber = operatorReportingService.getTaskNumber(task)
        const createContext: OperatorReportingCreateContext = {
            taskId: task.id ?? null,
            taskNumber,
            processId: payload?.processId ?? task.productionProcessId ?? task.productionProcessFB?.id ?? null,
            processName: payload?.processName ?? task.productionProcessFB?.name ?? null,
            plateNumber: payload?.plateNumber ?? task.plateNumber ?? task.procPlateNumber ?? null,
            printVariant: task.printVariant ?? null,
            outOfSequenceCodeId: payload?.outOfSequenceCodeId ?? null,
            origin: REPORTS.DETAIL_ORIGINS.OPERATOR_REPORTING
        }

        setSelectedTask(task)
        reportExecutionStore.setOperatorReportingCreateContext(createContext)

        return createContext
    }

    const resetRuntimeContext = (payload?: { preserveCreateContext?: boolean; preserveActiveSession?: boolean }): void => {
        // Home resets on unmount while the operator moves on to the report it opened - the session and the accident form
        // request for that report outlive it, only leaving the flow drops them.
        if (!payload?.preserveActiveSession) {
            state.activeSession.value = null
            state.accidentFormRequested.value = false
        }

        state.unfinishedReport.value = null
        state.processReportSwitchRequest.value = null
        state.interruptedReports.value = []
        state.interruptedReportsLoading.value = false
        state.reportOrderJobs.value = []
        state.reportOrderJobsLoading.value = false
        invalidateTaskSearch()
        invalidateScheduledTasksRequests()
        resetOrdersBranchState()
        state.scheduledTasks.value = []
        state.scheduledTasksLoading.value = false
        state.taskSearchResults.value = []
        state.taskSearchLoading.value = false
        state.reportProcesses.value = []
        state.outOfSequenceCodes.value = []
        state.selectedTask.value = null
        state.taskSearch.value = null

        if (!payload?.preserveCreateContext) {
            reportExecutionStore.setOperatorReportingCreateContext(null)
        }
    }

    return {
        initializeWorkspaceSelection,
        setAvailableWorkspaces,
        setAvailableWorkspacesLoading,
        setBootstrapLoading,
        setSelectedWorkspace,
        setSelectedWorkspaceDetails,
        setSelectedWorkspaceId,
        setActiveSession,
        setUnfinishedReport,
        setInterruptedReports,
        setInterruptedReportsLoading,
        setReportOrderJobs,
        setReportOrderJobsLoading,
        setShippingGroups,
        setReportItemOrders,
        setReportItemOrdersLoading,
        setReportItemOrdersFilters,
        setScheduledTasks,
        setScheduledTasksLoading,
        setTaskSearchResults,
        setTaskSearchLoading,
        setReportProcesses,
        setOutOfSequenceCodes,
        setSelectedTask,
        setTaskSearch,
        invalidateTaskSearch,
        clearSelectedTaskContext,
        resetReportItemOrdersFilters,
        resetOrdersBranchState,
        clearWorkspaceSelection,
        loadAvailableWorkspaces,
        loadSelectedWorkspace,
        selectWorkspace,
        loadUnfinishedSession,
        loadUnfinishedReport,
        loadInterruptedReports,
        loadHomeInitialData,
        ensureOperatorActionsContext,
        notifyPlanChanged,
        setOperatorActionsDialogOpen,
        setRunningReport,
        requestProcessReportSwitch,
        takeProcessReportSwitchRequest,
        requestAccidentForm,
        takeAccidentFormRequest,
        startReportingSession,
        stopReportingSession,
        finalizeStoppedSession,
        loadStoppedSessionDetails,
        getAllowedReportItemTypeIds,
        loadReportProcesses,
        loadOutOfSequenceCodes,
        loadShippingGroups,
        loadReportItemOrders,
        loadScheduledTasks,
        loadReportingParameterColumns,
        searchTasks,
        searchTaskByNumber,
        loadReportOrderJobs,
        saveCreateReportContext,
        resetRuntimeContext
    }
}
